import { useEffect, useState } from 'react';

import { Dropdown, ShimmerDiv, showSnackbar, TabPage, TabView } from 'fluxo-ui';
import { BarChartIcon, CalendarIcon, LineChartUpIcon, PlayIcon, UserGroupIcon } from 'fluxo-ui/icons';

import { BarChart, DonutChart } from '../components/charts';
import analyticsService from '../services/analytics.service';
import examService from '../services/exam.service';

interface ExamOption {
    label: string;
    value: string | null;
}

interface ExamAnalyticsData {
    totalAttempts: number;
    completions: number;
    averageScore: number;
    passRate: number;
    medianScore: number;
    averageTime: number;
    scoreDistribution: { range: string; count: number }[];
    questionPerformance: any[];
    [key: string]: any;
}

const chipClasses: Record<string, string> = {
    blue: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
    green: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    violet: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400',
    amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
};

/** Difficulty index = % of students answering correctly. Higher = easier. */
const difficultyChip = (index: number) => {
    if (index >= 70) return { label: 'Easy', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' };
    if (index >= 40) return { label: 'Moderate', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' };
    return { label: 'Hard', cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' };
};

/** Discrimination index = how well the question separates high/low performers. */
const discriminationChip = (index: number) => {
    if (index >= 30) return { label: 'Good', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' };
    if (index >= 20) return { label: 'Fair', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' };
    return { label: 'Poor', cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' };
};

const AnalyticsPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [selectedExam, setSelectedExam] = useState<string | null>(null);
    const [examOptions, setExamOptions] = useState<ExamOption[]>([]);
    const [examAnalytics, setExamAnalytics] = useState<ExamAnalyticsData | null>(null);
    const [dashboardStats, setDashboardStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [examLoading, setExamLoading] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (selectedExam) {
            fetchExamAnalytics(selectedExam);
        } else {
            setExamAnalytics(null);
        }
    }, [selectedExam]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [stats, examsData] = await Promise.all([analyticsService.getDashboardStats(), examService.listExams({ limit: 100 })]);
            setDashboardStats(stats);

            const exams = examsData.exams || examsData || [];
            const options: ExamOption[] = [
                { label: 'All Exams', value: null },
                ...exams.map((exam: any) => ({
                    label: exam.title,
                    value: exam._id,
                })),
            ];
            setExamOptions(options);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to load analytics data', 'Error', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const fetchExamAnalytics = async (examId: string) => {
        try {
            setExamLoading(true);
            const data = await analyticsService.getExamAnalytics(examId);
            setExamAnalytics(data as unknown as ExamAnalyticsData);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to load exam analytics', 'Error', { type: 'error' });
        } finally {
            setExamLoading(false);
        }
    };

    const stats = [
        {
            label: 'Exams Created',
            value: dashboardStats?.examsCreated ?? 0,
            icon: CalendarIcon,
            color: 'blue',
        },
        {
            label: 'Exams Attempted',
            value: dashboardStats?.examsAttempted ?? 0,
            icon: PlayIcon,
            color: 'green',
        },
        {
            label: 'Average Score',
            value: `${dashboardStats?.averageScore ?? 0}%`,
            icon: BarChartIcon,
            color: 'violet',
        },
        {
            label: 'Total Attempts',
            value: dashboardStats?.totalAttempts ?? 0,
            icon: UserGroupIcon,
            color: 'amber',
        },
    ];

    if (loading) {
        return (
            <div className="space-y-6">
                <ShimmerDiv level={2} className="h-16 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, i) => (
                        <ShimmerDiv key={i} level={3} className="h-28" />
                    ))}
                </div>
                <ShimmerDiv level={2} className="h-96 w-full" />
            </div>
        );
    }

    const completions = examAnalytics?.completions ?? 0;
    const passedCount = examAnalytics ? Math.round(((examAnalytics.passRate || 0) / 100) * completions) : 0;
    const failedCount = Math.max(0, completions - passedCount);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-1">Analytics</h1>
                <p className="text-slate-600 dark:text-slate-400">Performance insights and metrics from your data</p>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Dropdown
                        options={examOptions}
                        value={selectedExam}
                        onChange={(e) => setSelectedExam(e.value)}
                        placeholder="Select exam for detailed analytics"
                        showClear
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <div
                            key={index}
                            className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow p-5"
                        >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${chipClasses[stat.color]}`}>
                                <Icon className="w-5 h-5" />
                            </div>
                            <div className="mt-4 text-3xl font-bold text-slate-900 dark:text-white tabular-nums">{stat.value}</div>
                            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{stat.label}</div>
                        </div>
                    );
                })}
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                    <TabPage header="Exam Performance" leftIcon={BarChartIcon}>
                        <div className="p-6">
                            {examLoading ? (
                                <ShimmerDiv level={2} className="h-64 w-full" />
                            ) : examAnalytics ? (
                                <>
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                        <div className="lg:col-span-2">
                                            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
                                                Score Distribution
                                            </h3>
                                            <BarChart
                                                data={examAnalytics.scoreDistribution.map((item) => ({
                                                    label: `${item.range}%`,
                                                    value: item.count,
                                                }))}
                                                formatValue={(v) => `${v}`}
                                            />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Pass / Fail</h3>
                                            <DonutChart
                                                data={[
                                                    { label: 'Passed', value: passedCount, colorIndex: 1 },
                                                    { label: 'Failed', value: failedCount, colorIndex: 4 },
                                                ]}
                                                size={140}
                                                centerSub="Completed"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                                            <div className="text-sm text-slate-500 dark:text-slate-400">Average Score</div>
                                            <div className="text-2xl font-bold text-slate-900 dark:text-white mt-1 tabular-nums">
                                                {examAnalytics.averageScore}%
                                            </div>
                                        </div>
                                        <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                                            <div className="text-sm text-slate-500 dark:text-slate-400">Median Score</div>
                                            <div className="text-2xl font-bold text-slate-900 dark:text-white mt-1 tabular-nums">
                                                {examAnalytics.medianScore}%
                                            </div>
                                        </div>
                                        <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                                            <div className="text-sm text-slate-500 dark:text-slate-400">Pass Rate</div>
                                            <div className="text-2xl font-bold text-slate-900 dark:text-white mt-1 tabular-nums">
                                                {examAnalytics.passRate}%
                                            </div>
                                        </div>
                                        <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                                            <div className="text-sm text-slate-500 dark:text-slate-400">Avg Time</div>
                                            <div className="text-2xl font-bold text-slate-900 dark:text-white mt-1 tabular-nums">
                                                {Math.round(examAnalytics.averageTime / 60)}m
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center text-slate-500 dark:text-slate-400 py-16">
                                    <BarChartIcon className="w-14 h-14 mx-auto mb-4 opacity-40" />
                                    <p className="font-medium text-slate-900 dark:text-white">No exam selected</p>
                                    <p className="text-sm mt-1">Select an exam above to view detailed performance analytics</p>
                                </div>
                            )}
                        </div>
                    </TabPage>

                    <TabPage header="Question Analysis" leftIcon={LineChartUpIcon}>
                        <div className="p-6">
                            {examLoading ? (
                                <ShimmerDiv level={2} className="h-64 w-full" />
                            ) : examAnalytics && examAnalytics.questionPerformance.length > 0 ? (
                                <>
                                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Question Performance</h3>
                                    <div className="space-y-2">
                                        {examAnalytics.questionPerformance.map((q: any, i: number) => {
                                            const difficulty = difficultyChip(q.difficultyIndex ?? 0);
                                            const discrimination = discriminationChip(q.discriminationIndex ?? 0);
                                            return (
                                                <div
                                                    key={i}
                                                    className="flex flex-wrap items-center gap-3 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-700/30"
                                                >
                                                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-900 dark:text-white tabular-nums shrink-0">
                                                        {i + 1}
                                                    </span>
                                                    <div className="flex-1 min-w-[10rem]">
                                                        <div className="text-sm font-medium text-slate-900 dark:text-white">
                                                            Question #{i + 1}
                                                        </div>
                                                        <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                                                            Avg score {q.averageScore?.toFixed(1)}%
                                                        </div>
                                                    </div>
                                                    <span
                                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium tabular-nums ${difficulty.cls}`}
                                                        title="Difficulty index — % of students who answered correctly"
                                                    >
                                                        {difficulty.label} · {q.difficultyIndex?.toFixed(1)}%
                                                    </span>
                                                    <span
                                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium tabular-nums ${discrimination.cls}`}
                                                        title="Discrimination index — how well the question separates high and low performers"
                                                    >
                                                        {discrimination.label} · {q.discriminationIndex?.toFixed(1)}%
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-6 p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl text-sm text-teal-800 dark:text-teal-200">
                                        <span className="font-semibold">Difficulty</span> is the share of students answering correctly
                                        (higher = easier). <span className="font-semibold">Discrimination</span> measures how well the
                                        question separates high and low performers (higher = better).
                                    </div>
                                </>
                            ) : (
                                <div className="text-center text-slate-500 dark:text-slate-400 py-16">
                                    <LineChartUpIcon className="w-14 h-14 mx-auto mb-4 opacity-40" />
                                    <p className="font-medium text-slate-900 dark:text-white">
                                        {selectedExam ? 'No question data' : 'No exam selected'}
                                    </p>
                                    <p className="text-sm mt-1">
                                        {selectedExam
                                            ? 'No question performance data available for this exam'
                                            : 'Select an exam to view question analysis'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </TabPage>

                    <TabPage header="Overview" leftIcon={UserGroupIcon}>
                        <div className="p-6">
                            {examAnalytics ? (
                                <div className="space-y-6">
                                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Exam Summary</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {[
                                            { label: 'Total Attempts', value: examAnalytics.totalAttempts },
                                            { label: 'Completions', value: examAnalytics.completions },
                                            { label: 'Average Score', value: `${examAnalytics.averageScore}%` },
                                            { label: 'Pass Rate', value: `${examAnalytics.passRate}%` },
                                            { label: 'Questions Analyzed', value: examAnalytics.questionPerformance.length },
                                            { label: 'Avg. Completion Time', value: `${Math.round(examAnalytics.averageTime / 60)}m` },
                                        ].map((item, index) => (
                                            <div
                                                key={index}
                                                className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-700/30"
                                            >
                                                <div className="text-sm text-slate-500 dark:text-slate-400">{item.label}</div>
                                                <div className="text-2xl font-bold text-slate-900 dark:text-white mt-1 tabular-nums">
                                                    {item.value}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center text-slate-500 dark:text-slate-400 py-16">
                                    <UserGroupIcon className="w-14 h-14 mx-auto mb-4 opacity-40" />
                                    <p className="font-medium text-slate-900 dark:text-white">No exam selected</p>
                                    <p className="text-sm mt-1">Select an exam to view overview statistics</p>
                                </div>
                            )}
                        </div>
                    </TabPage>
                </TabView>
            </div>
        </div>
    );
};

export default AnalyticsPage;
